import React, { useState, useEffect } from 'react';
import {
    ChevronLeft,
    Save,
    Calendar,
    MapPin,
    User,
    Hash,
    CheckCircle2,
    XCircle,
    Camera,
    Info,
    AlertCircle,
    Upload,
    X,
    Image as ImageIcon
} from 'lucide-react';
import { useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import { Combobox } from '../../../components/ui/combobox';
import api from '../../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../../components/ui/utils';

interface ElectricalInspectionFormProps {
    id?: number;
    initialAssetId?: string;
    onBack: () => void;
    onSaveSuccess: () => void;
}

const ElectricalInspectionForm: React.FC<ElectricalInspectionFormProps> = ({ id, initialAssetId, onBack, onSaveSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [assets, setAssets] = useState<any[]>([]);
    const [formData, setFormData] = useState({
        assetId: initialAssetId || '',
        location: '',
        testDate: new Date().toISOString().split('T')[0],
        nextTestDue: '',
        testResult: 'Pass',
        nextAction: 'Returned to Service',
        testedBy: '',
        licenceNo: '',
        comments: ''
    });

    const [photos, setPhotos] = useState<File[]>([]);
    const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selected = Array.from(e.target.files);
            const validFiles = selected.filter(f => f.size <= 5 * 1024 * 1024);
            if (validFiles.length < selected.length) {
                toast.error("Some files were skipped as they exceed 5MB");
            }
            setPhotos(prev => [...prev, ...validFiles]);
        }
    };

    const removePhoto = (index: number) => {
        setPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingPhoto = (index: number) => {
        setExistingPhotos(prev => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        fetchAssets();
        if (id) {
            fetchInspection();
        }
    }, [id]);

    const fetchAssets = async () => {
        try {
            const result = await api.get('/api/assets?addToElectricalRegister=true');
            setAssets(result);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchInspection = async () => {
        try {
            setLoading(true);
            const result = await api.get(`/api/assets/electrical-inspections/${id}`);
            setFormData({
                ...result,
                assetId: result.asset?.id.toString() || ''
            });
            if (result.photos && Array.isArray(result.photos)) {
                setExistingPhotos(result.photos.map((p: any) => p.photoUrl));
            }
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch inspection details');
        } finally {
            setLoading(false);
        }
    };

    // Auto-calculate next test due
    useEffect(() => {
        if (!id && formData.assetId && formData.testDate) {
            calculateNextDue();
        }
    }, [formData.assetId, formData.testDate, assets]);

    const calculateNextDue = async () => {
        try {
            const asset = assets.find(a => a.id.toString() === formData.assetId);
            if (!asset) return;

            let cadenceNumber = asset.electricalCadenceNumber;
            let cadenceUnit = asset.electricalCadenceUnit;

            if (!cadenceNumber) {
                const settings = await api.get('/api/assets/settings/electrical');
                cadenceNumber = settings.defaultElectricalCadenceNumber || 30;
                cadenceUnit = settings.defaultElectricalCadenceUnit || 'days';
            }

            const nextDate = new Date(formData.testDate);
            const num = parseInt(cadenceNumber.toString());

            if (cadenceUnit === 'days') {
                nextDate.setDate(nextDate.getDate() + num);
            } else if (cadenceUnit === 'weeks') {
                nextDate.setDate(nextDate.getDate() + (num * 7));
            } else if (cadenceUnit === 'months') {
                nextDate.setMonth(nextDate.getMonth() + num);
            }

            setFormData(prev => ({ ...prev, nextTestDue: nextDate.toISOString().split('T')[0] }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.assetId) {
            toast.error('Please select an asset');
            return;
        }
        try {
            setLoading(true);
            let uploadedPhotoUrls: string[] = [...existingPhotos];
            if (photos.length > 0) {
                const uploadFormData = new FormData();
                photos.forEach(p => uploadFormData.append('photos', p));
                const uploadRes = await api.post('/api/assets/electrical-inspections/upload-photos', uploadFormData);
                if (uploadRes.urls) {
                    uploadedPhotoUrls = [...uploadedPhotoUrls, ...uploadRes.urls];
                }
            }

            const payload = {
                ...formData,
                asset: { id: parseInt(formData.assetId) },
                photos: uploadedPhotoUrls
            };

            if (id) {
                await api.patch(`/api/assets/electrical-inspections/${id}`, payload);
            } else {
                await api.post('/api/assets/electrical-inspections', payload);
            }

            toast.success(id ? 'Inspection updated' : 'Inspection recorded');
            onSaveSuccess();
        } catch (err) {
            console.error(err);
            toast.error('Failed to save inspection');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                    onClick={onBack}
                >
                    <ChevronLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{id ? 'Edit Inspection' : 'New Electrical Inspection'}</h1>
                    <p className="text-gray-500 font-medium text-sm">Fixed-format Test & Tag record</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Asset & Location Information */}
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <h3 className="text-xs font-black text-gray-400 mt-2 uppercase tracking-widest px-1">Equipment Details</h3>

                            <div className="space-y-1.5 px-1">
                                <label className="text-xs font-bold text-gray-700">Select Asset</label>
                                {id ? (
                                    <div className="h-11 flex items-center px-4 rounded-xl border border-gray-100 bg-gray-50 text-sm font-medium text-gray-600">
                                        {assets.find(a => a.id.toString() === formData.assetId)?.name || 'Loading...'}
                                    </div>
                                ) : (
                                    <Combobox
                                        options={assets.map(a => ({ label: `${a.name} (${a.tag})`, value: a.id.toString() }))}
                                        value={formData.assetId}
                                        onChange={(v) => setFormData({ ...formData, assetId: v || '' })}
                                        placeholder="Select asset..."
                                        searchPlaceholder="Search asset name or tag..."
                                        emptyMessage="No asset found."
                                    />
                                )}
                            </div>

                            <div className="space-y-1.5 px-1">
                                <label className="text-xs font-bold text-gray-700">Location</label>
                                <div className="relative group">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={16} />
                                    <Input
                                        placeholder="e.g. Floor 2, Zone B"
                                        className="pl-10 h-11 rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <h3 className="text-xs font-black text-gray-400 mt-2 uppercase tracking-widest px-1">Inspection Context</h3>

                            <div className="space-y-1.5 px-1">
                                <label className="text-xs font-bold text-gray-700">Tested By</label>
                                <div className="relative group">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={16} />
                                    <Input
                                        placeholder="Full name of inspector"
                                        className="pl-10 h-11 rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.testedBy}
                                        onChange={(e) => setFormData({ ...formData, testedBy: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5 px-1">
                                <label className="text-xs font-bold text-gray-700">Inspector Licence No</label>
                                <div className="relative group">
                                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={16} />
                                    <Input
                                        placeholder="Optional licence number"
                                        className="pl-10 h-11 rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.licenceNo}
                                        onChange={(e) => setFormData({ ...formData, licenceNo: e.target.value })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Test Results & Scheduling */}
                    <div className="space-y-6">
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <h3 className="text-xs font-black text-gray-400 mt-2 uppercase tracking-widest px-1">Test Results</h3>

                            <div className="flex gap-4 px-1">
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, testResult: 'Pass', nextAction: 'Returned to Service' })}
                                    className={cn(
                                        "flex-1 p-4 rounded-2xl border transition-all flex flex-col items-center gap-2",
                                        formData.testResult === 'Pass'
                                            ? "bg-green-50 border-green-200 text-green-700 ring-2 ring-green-100"
                                            : "bg-white border-gray-100 text-gray-400 hover:border-gray-200"
                                    )}
                                >
                                    <CheckCircle2 size={24} />
                                    <span className="text-sm font-bold">PASS</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, testResult: 'Fail', nextAction: 'Removed from Service' })}
                                    className={cn(
                                        "flex-1 p-4 rounded-2xl border transition-all flex flex-col items-center gap-2",
                                        formData.testResult === 'Fail'
                                            ? "bg-red-50 border-red-200 text-red-700 ring-2 ring-red-100"
                                            : "bg-white border-gray-100 text-gray-400 hover:border-gray-200"
                                    )}
                                >
                                    <XCircle size={24} />
                                    <span className="text-sm font-bold">FAIL</span>
                                </button>
                            </div>

                            <div className="space-y-1.5 px-1">
                                <label className="text-xs font-bold text-gray-700">Next Action</label>
                                <Select value={formData.nextAction} onValueChange={(v) => setFormData({ ...formData, nextAction: v })}>
                                    <SelectTrigger className="h-11 rounded-xl border-gray-100 bg-gray-50/50">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-xl">
                                        <SelectItem value="Returned to Service">Returned to Service</SelectItem>
                                        <SelectItem value="Removed from Service">Removed from Service</SelectItem>
                                        <SelectItem value="Sent for Repair">Sent for Repair</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <h3 className="text-xs font-black text-gray-400 mt-2 uppercase tracking-widest px-1">Scheduling</h3>

                            <div className="grid grid-cols-2 gap-4 px-1">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-gray-700">Test Date</label>
                                    <Input
                                        type="date"
                                        className="h-11 rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.testDate}
                                        onChange={(e) => setFormData({ ...formData, testDate: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-xs font-bold text-gray-700">Next Due</label>
                                    <Input
                                        type="date"
                                        className="h-11 rounded-xl border-gray-100 bg-gray-50/50"
                                        value={formData.nextTestDue}
                                        onChange={(e) => setFormData({ ...formData, nextTestDue: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="flex items-start gap-2 text-[10px] text-blue-500 font-medium bg-blue-50/50 p-3 rounded-xl mx-1">
                                <Info size={14} className="shrink-0" />
                                <p>Next Due is calculated based on the asset's cadence or global default setting. You can override it manually.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Info */}
                <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                    <h3 className="text-xs font-black text-gray-400 mt-2 uppercase tracking-widest px-1">Comments & Documentation</h3>

                    <div className="space-y-4 px-1">
                        <textarea
                            className="w-full min-h-[120px] rounded-2xl border-gray-100 bg-gray-50/50 p-4 text-sm focus:bg-white transition-all resize-none outline-none focus:ring-2 focus:ring-blue-100"
                            placeholder="Add any additional notes about the test performance or visual inspection..."
                            value={formData.comments}
                            onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                        />

                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <ImageIcon size={24} className="text-gray-900" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Photos</h2>
                                    <p className="text-gray-500 text-xs font-medium">Add photos to document the inspection (optional)</p>
                                </div>
                            </div>

                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full h-11 rounded-xl border-gray-100 text-gray-700 bg-white hover:bg-gray-50 font-bold gap-2"
                            >
                                <Upload size={16} />
                                Upload Photo
                            </Button>
                            <p className="text-xs text-gray-400 font-medium tracking-wide">Maximum file size: 5MB</p>
                            <input
                                type="file"
                                multiple
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept="image/*"
                                className="hidden"
                            />

                            {(photos.length > 0 || existingPhotos.length > 0) && (
                                <div className="flex flex-wrap gap-4 mt-6 pt-2">
                                    {photos.map((photo, idx) => (
                                        <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                                            <img
                                                src={URL.createObjectURL(photo)}
                                                alt="preview"
                                                className="w-full h-full object-cover"
                                                onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removePhoto(idx)}
                                                className="absolute top-1 right-1 bg-white p-1 rounded-full shadow-sm hover:bg-gray-100 opacity-80 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X size={12} className="text-gray-900" />
                                            </button>
                                        </div>
                                    ))}

                                    {existingPhotos.map((url, idx) => (
                                        <div key={`existing-${idx}`} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                                            <img
                                                src={url}
                                                alt="existing preview"
                                                className="w-full h-full object-cover"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeExistingPhoto(idx)}
                                                className="absolute top-1 right-1 bg-white p-1 rounded-full shadow-sm hover:bg-gray-100 opacity-80 group-hover:opacity-100 transition-opacity"
                                            >
                                                <X size={12} className="text-gray-900" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-4 pb-12">
                    <Button type="button" variant="ghost" onClick={onBack} className="h-12 px-8 rounded-xl font-bold">Cancel</Button>
                    <Button type="submit" disabled={loading} className="h-12 px-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold gap-2 shadow-lg shadow-blue-200 transition-all">
                        {loading ? 'Saving...' : (
                            <>
                                <Save size={18} />
                                {id ? 'Update Inspection' : 'Save Inspection'}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default ElectricalInspectionForm;
